import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { AlertTriangle, CheckCircle2, ClipboardCheck, Clock, LogIn, LogOut } from 'lucide-react';

import Sidebar from '../layouts/Sidebar';
import Modal from '../components/ui/Modal';
import { PageLoader } from '../components/ui/Spinner';
import CommandCenterService from '../services/CommandCenterService';
import AnnouncementMemberService from '../services/AnnouncementMemberService';
import { buildRoster, createSerialQueue } from '../utils/utils';
import {
  PPE_WARNING_MINUTES, DONNING_CHECKLIST_ITEMS, ppeRemainingMinutes, checklistDoneCount, hasPendingDoffing,
  zoneEntryBlocker, DonningModal, DoffingModal,
} from '../components/Incident/PpeScreening';

// The suit-up cycle, left to right: waiting on the Team Leader → donning → in the zone →
// doffing screening → back to donning for the next rotation.
const COLUMNS = [
  { id: 'pending', title: 'Pending', hint: 'Waiting on the Team Leader' },
  { id: 'donning', title: 'Donning', hint: 'Pre-entry check' },
  { id: 'inZone', title: 'In Zone', hint: 'Suited up and inside' },
  { id: 'doffing', title: 'Doffing', hint: 'Post-exit screening' },
];
const COLUMN_TITLE = Object.fromEntries(COLUMNS.map((c) => [c.id, c.title]));

function columnOf(m) {
  if (m.ppeDonnedAt) return 'inZone';
  if (hasPendingDoffing(m)) return 'doffing';
  if (!m.activeSince || !m.zoneName || m.zoneName === 'Cold Zone') return 'pending';
  return 'donning';
}

function pendingReason(m) {
  if (!m.activeSince) return 'Not arrived yet';
  return 'No Hot/Warm zone assigned';
}

// Minutes rested since the last doffing screening was closed out — null if they haven't
// done a rotation yet (or are mid-rotation).
function restMinutes(m, now) {
  if (!m.doffingCompletedAt || m.ppeDonnedAt || hasPendingDoffing(m)) return null;
  return Math.max(0, Math.floor((now - new Date(m.doffingCompletedAt).getTime()) / 60000));
}

// Rest should at least match the last time spent in the zone before the next entry.
function restShortfall(m, now) {
  const rested = restMinutes(m, now);
  if (rested === null || !m.lastZoneMinutes) return null;
  return rested < m.lastZoneMinutes ? { rested, needed: m.lastZoneMinutes } : null;
}

function formatTime(iso) {
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

// Entry Control Officer's board. Arrival, zone assignment and on-scene roles stay with the
// Team Leader — the server only lets an ECO write the donning/doffing fields. Cards move
// by their one button or by drag and drop; either way every move goes through the same
// checks (a complete checklist to enter, a closed-out screening to leave Doffing).
export default function EntryControl() {
  const [incident, setIncident] = useState(null);
  const [roster, setRoster] = useState([]);
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(() => Date.now());
  const [donningMember, setDonningMember] = useState(null);
  const [doffingMember, setDoffingMember] = useState(null);
  const [restConfirm, setRestConfirm] = useState(null); // { m, rested, needed } awaiting "send in anyway"
  const warnedRef = useRef(new Set());
  const queueRef = useRef(createSerialQueue()); // one responder's updates reach the server in order

  useEffect(() => {
    const tickId = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(tickId);
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const commandResponse = await CommandCenterService.getStats();
        const activeIncident = commandResponse?.data?.activeIncident || null;
        setIncident(activeIncident);
        if (activeIncident) {
          const { entity } = await AnnouncementMemberService.findById(activeIncident.id);
          setRoster(buildRoster(entity || [], activeIncident.lat, activeIncident.lng));
        } else {
          setRoster([]);
        }
      } catch (error) {
        console.log(error);
      }
    };
    load().finally(() => setLoading(false));
    const intervalId = setInterval(load, 15000);
    return () => clearInterval(intervalId);
  }, []);

  // Once per donning session, the moment a responder's PPE time crosses the warning line.
  useEffect(() => {
    roster.forEach((m) => {
      if (!m.ppeDonnedAt) return;
      const remaining = ppeRemainingMinutes(m.ppeDonnedAt, now, incident?.ppeSafeLimitMinutes);
      const key = `${m.id}-${m.ppeDonnedAt}`;
      if (remaining <= PPE_WARNING_MINUTES && !warnedRef.current.has(key)) {
        warnedRef.current.add(key);
        toast.warning(`${m.name}'s PPE has ~${remaining} min remaining — call them out.`);
      }
    });
  }, [now, roster, incident]);

  const patchMember = async (id, patch) => {
    const previous = roster.find((m) => m.id === id);
    setRoster((prev) => prev.map((m) => (m.id === id ? { ...m, ...patch } : m)));
    try {
      await queueRef.current(id, () => AnnouncementMemberService.update(id, patch));
    } catch (error) {
      console.log(error);
      if (previous) setRoster((prev) => prev.map((m) => (m.id === id ? previous : m)));
      toast.error(error?.response?.data?.message || 'Failed to save. Please retry.');
    }
  };

  const saveDonning = (patch) => {
    const member = donningMember;
    setDonningMember(null);
    if (member) patchMember(member.id, patch);
  };

  // Warn-but-allow: someone who hasn't rested as long as their last entry lasted can still
  // be sent in, but only after the ECO confirms.
  const enterZone = (m, { confirmedRest = false } = {}) => {
    const blocker = zoneEntryBlocker(m);
    if (blocker) {
      toast.error(`${m.name}: ${blocker}`);
      return;
    }
    const shortfall = confirmedRest ? null : restShortfall(m, Date.now());
    if (shortfall) {
      setRestConfirm({ m, ...shortfall });
      return;
    }
    patchMember(m.id, { ppeDonnedAt: new Date().toISOString() });
  };

  // Exiting stops the PPE timer and opens the doffing screening straight away.
  const exitZone = (m) => {
    const exitedZoneAt = new Date().toISOString();
    // The server records lastZoneMinutes itself on exit; mirror it locally so the rest
    // comparison is right before the next roster refresh.
    const lastZoneMinutes = Math.max(0, Math.round((Date.now() - new Date(m.ppeDonnedAt).getTime()) / 60000));
    patchMember(m.id, { ppeDonnedAt: null, exitedZoneAt });
    setRoster((prev) => prev.map((r) => (r.id === m.id ? { ...r, lastZoneMinutes } : r)));
    setDoffingMember({ ...m, ppeDonnedAt: null, exitedZoneAt });
  };

  // Closing out the screening sends them back to Donning (the patch resets the
  // checklist — see DoffingModal).
  const saveDoffing = (patch) => {
    const member = doffingMember;
    setDoffingMember(null);
    if (member) patchMember(member.id, patch);
  };

  // Drag and drop is a shortcut for the card's own button — same rules, same forms.
  const onDragEnd = ({ draggableId, source, destination }) => {
    if (!destination || destination.droppableId === source.droppableId) return;
    const m = roster.find((r) => String(r.id) === draggableId);
    if (!m) return;
    const move = `${source.droppableId}>${destination.droppableId}`;
    if (move === 'donning>inZone') {
      const blocker = zoneEntryBlocker(m);
      if (blocker && checklistDoneCount(m.donningChecklist) < DONNING_CHECKLIST_ITEMS.length) {
        toast.info(`Finish ${m.name}'s donning check first.`);
        setDonningMember(m);
      } else {
        enterZone(m);
      }
    } else if (move === 'inZone>doffing') {
      exitZone(m);
    } else if (move === 'doffing>donning') {
      setDoffingMember(m); // closing out the screening is what moves them back
    } else {
      toast.error(`${m.name} can't move from ${COLUMN_TITLE[source.droppableId]} to ${COLUMN_TITLE[destination.droppableId]}.`);
    }
  };

  if (loading) {
    return (
      <Sidebar title="Entry Control">
        <PageLoader label="Loading entry control…" />
      </Sidebar>
    );
  }

  const limit = incident?.ppeSafeLimitMinutes;
  const remainingOf = (m) => ppeRemainingMinutes(m.ppeDonnedAt, now, limit);
  const byColumn = { pending: [], donning: [], inZone: [], doffing: [] };
  roster.forEach((m) => byColumn[columnOf(m)].push(m));
  byColumn.inZone.sort((a, b) => remainingOf(a) - remainingOf(b));
  byColumn.doffing.sort((a, b) => new Date(a.exitedZoneAt) - new Date(b.exitedZoneAt));
  byColumn.donning.sort((a, b) => checklistDoneCount(b.donningChecklist) - checklistDoneCount(a.donningChecklist));
  byColumn.pending.sort((a, b) => a.name.localeCompare(b.name));
  const isReady = (m) => zoneEntryBlocker(m) === null;
  const lowInZone = byColumn.inZone.filter((m) => remainingOf(m) <= PPE_WARNING_MINUTES).length;
  const ready = byColumn.donning.filter(isReady).length;

  return (
    <Sidebar title="Entry Control">
      <section className="space-y-4">
        <div className="bg-white rounded-xl border border-neutral-200 shadow-subtle p-5 dark:bg-ink-900 dark:border-neutral-700">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h2 className="text-lg font-bold text-neutral-900 dark:text-neutral-50">{incident ? incident.title : 'No active incident'}</h2>
            {incident && (
              <p className="text-sm text-neutral-500">
                PPE safe operating time: <span className="font-semibold text-neutral-700 dark:text-neutral-200">{limit || 30} min</span>
              </p>
            )}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
            <Stat label={lowInZone ? `In zone · ${lowInZone} low on PPE time` : 'In zone'} value={byColumn.inZone.length} tone={lowInZone ? 'red' : 'orange'} />
            <Stat label="Doffing pending" value={byColumn.doffing.length} tone={byColumn.doffing.length ? 'red' : 'neutral'} />
            <Stat label="Ready to enter" value={ready} tone="green" />
            <Stat label="Preparing" value={byColumn.donning.length - ready + byColumn.pending.length} tone="neutral" />
          </div>
        </div>

        {roster.length === 0 ? (
          <div className="bg-white rounded-xl border border-neutral-200 p-10 text-center text-sm text-neutral-400 dark:bg-ink-900 dark:border-neutral-700">
            {incident ? 'No responders assigned to this incident yet.' : 'Entry control opens when an incident is active.'}
          </div>
        ) : (
          <DragDropContext onDragEnd={onDragEnd}>
            <div className="overflow-x-auto pb-2">
              <div className="grid gap-3 min-w-[820px]" style={{ gridTemplateColumns: 'minmax(150px, 0.7fr) repeat(3, minmax(210px, 1fr))' }}>
                {COLUMNS.map((col) => (
                  <Column key={col.id} col={col} count={byColumn[col.id].length}>
                    {byColumn[col.id].map((m, index) => (
                      <Draggable key={m.id} draggableId={String(m.id)} index={index} isDragDisabled={col.id === 'pending'}>
                        {(provided, snapshot) => (
                          <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}
                            className={snapshot.isDragging ? 'rotate-1 shadow-lg' : ''}>
                            {col.id === 'pending' && <PendingCard m={m} />}
                            {col.id === 'donning' && <DonningCard m={m} now={now} onOpen={() => setDonningMember(m)} onEnter={() => enterZone(m)} />}
                            {col.id === 'inZone' && <InZoneCard m={m} remaining={remainingOf(m)} onExit={() => exitZone(m)} />}
                            {col.id === 'doffing' && <DoffingCard m={m} onOpen={() => setDoffingMember(m)} />}
                          </div>
                        )}
                      </Draggable>
                    ))}
                  </Column>
                ))}
              </div>
            </div>
          </DragDropContext>
        )}
      </section>

      <Modal
        open={!!restConfirm}
        onClose={() => setRestConfirm(null)}
        title="Rest not finished"
        subtitle={restConfirm ? `${restConfirm.m.name} has rested ${restConfirm.rested} of ${restConfirm.needed} min — as long as their last time in the zone.` : ''}
        confirmLabel="Send in anyway"
        onConfirm={() => {
          const { m } = restConfirm;
          setRestConfirm(null);
          enterZone(m, { confirmedRest: true });
        }}
      >
        <p className="text-sm text-neutral-600 dark:text-neutral-300">Sending them back in early is allowed, but check they're hydrated and fit first.</p>
      </Modal>
      <DonningModal member={donningMember} onClose={() => setDonningMember(null)} onSave={saveDonning} />
      <DoffingModal member={doffingMember} onClose={() => setDoffingMember(null)} onSave={saveDoffing} />
    </Sidebar>
  );
}

const STAT_TONES = {
  red: 'text-red-600 dark:text-red-400',
  orange: 'text-orange-600 dark:text-orange-400',
  green: 'text-green-600 dark:text-green-400',
  neutral: 'text-neutral-800 dark:text-neutral-100',
};

function Stat({ label, value, tone }) {
  return (
    <div className="rounded-lg bg-neutral-50 border border-neutral-100 px-4 py-3 dark:bg-ink-800 dark:border-neutral-700">
      <p className={`text-2xl font-bold ${STAT_TONES[tone]}`}>{value}</p>
      <p className="text-xs font-medium text-neutral-500">{label}</p>
    </div>
  );
}

function Column({ col, count, children }) {
  return (
    <Droppable droppableId={col.id} isDropDisabled={col.id === 'pending'}>
      {(provided, snapshot) => (
        <div className={`rounded-xl border p-3 flex flex-col min-h-[60vh] transition-colors ${snapshot.isDraggingOver ? 'bg-seha-50 border-seha-200 dark:bg-seha-500/10 dark:border-seha-500/30' : 'bg-neutral-50 border-neutral-200 dark:bg-ink-800/60 dark:border-neutral-700'}`}>
          <div className="flex items-baseline justify-between px-1 mb-3">
            <div>
              <h3 className="text-sm font-bold text-neutral-800 dark:text-neutral-100">{col.title}</h3>
              <p className="text-[11px] text-neutral-500">{col.hint}</p>
            </div>
            <span className="text-xs font-bold text-neutral-600 bg-white border border-neutral-200 rounded-full px-2 py-0.5 dark:bg-ink-900 dark:border-neutral-700 dark:text-neutral-300">{count}</span>
          </div>
          <div ref={provided.innerRef} {...provided.droppableProps} className="flex flex-col gap-2 flex-1">
            {children}
            {provided.placeholder}
          </div>
        </div>
      )}
    </Droppable>
  );
}

function CardShell({ tone = 'neutral', children }) {
  const tones = {
    neutral: 'border-neutral-200 dark:border-neutral-700',
    green: 'border-green-400',
    amber: 'border-amber-400',
    orange: 'border-orange-400',
    red: 'border-red-500',
  };
  return <div className={`bg-white rounded-lg border border-l-4 ${tones[tone]} p-3 space-y-2 dark:bg-ink-900`}>{children}</div>;
}

// Zone · on-scene roles, under the name.
function Who({ m }) {
  const detail = [m.zoneName, ...(m.roles || [])].filter(Boolean).join(' · ');
  return (
    <div className="min-w-0">
      <p className="font-bold text-sm text-neutral-800 truncate dark:text-neutral-100">{m.name}</p>
      {detail && <p className="text-xs text-neutral-500 truncate">{detail}</p>}
    </div>
  );
}

function PendingCard({ m }) {
  return (
    <div className="bg-white rounded-lg border border-neutral-200 px-3 py-2 dark:bg-ink-900 dark:border-neutral-700">
      <p className="text-sm font-semibold text-neutral-700 truncate dark:text-neutral-200">{m.name}</p>
      <p className="text-[11px] text-neutral-500">{pendingReason(m)}</p>
    </div>
  );
}

function DonningCard({ m, now, onOpen, onEnter }) {
  const done = checklistDoneCount(m.donningChecklist);
  const total = DONNING_CHECKLIST_ITEMS.length;
  const ready = done === total;
  const rested = restMinutes(m, now);
  const short = restShortfall(m, now);
  return (
    <CardShell tone={short ? 'amber' : ready ? 'green' : 'neutral'}>
      <Who m={m} />
      {rested !== null && (
        <div className="text-xs">
          <p className={`flex items-center gap-1.5 font-semibold whitespace-nowrap ${short ? 'text-amber-700 dark:text-amber-400' : 'text-green-700 dark:text-green-400'}`}>
            <Clock className="w-3.5 h-3.5 shrink-0" />
            {short ? `Resting ${rested} of ${short.needed} min` : `Rested ${rested} min ✓`}
          </p>
          {m.lastZoneMinutes ? <p className="text-neutral-500 pl-5">Last time in zone: {m.lastZoneMinutes} min</p> : null}
        </div>
      )}
      <div className="flex items-center gap-2">
        <div className="flex-1 h-1.5 rounded-full bg-neutral-100 overflow-hidden dark:bg-neutral-700">
          <div className={`h-full ${ready ? 'bg-green-500' : 'bg-seha-500'}`} style={{ width: `${(done / total) * 100}%` }} />
        </div>
        <span className="text-[11px] font-semibold text-neutral-500">{done}/{total}</span>
      </div>
      {ready ? (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <ActionButton icon={LogIn} label="Enter Zone" tone="blue" onClick={onEnter} />
          <button type="button" onClick={onOpen} className="whitespace-nowrap text-xs font-semibold text-neutral-500 hover:text-seha-700 dark:hover:text-seha-300">Review check</button>
        </div>
      ) : (
        <ActionButton icon={ClipboardCheck} label={done === 0 ? 'Start donning' : 'Continue donning'} tone="neutral" onClick={onOpen} />
      )}
    </CardShell>
  );
}

function InZoneCard({ m, remaining, onExit }) {
  const low = remaining <= PPE_WARNING_MINUTES;
  return (
    <CardShell tone={low ? 'red' : 'orange'}>
      <Who m={m} />
      <div className={`flex items-center gap-1.5 text-sm font-semibold ${low ? 'text-red-600 dark:text-red-400' : 'text-orange-600 dark:text-orange-400'}`}>
        {low ? <AlertTriangle className="w-4 h-4" /> : <Clock className="w-4 h-4" />}
        {remaining <= 0 ? 'PPE time expired' : `${remaining} min left`}
        <span className="ml-auto text-[11px] font-medium text-neutral-500">in {formatTime(m.ppeDonnedAt)}</span>
      </div>
      <ActionButton icon={LogOut} label="Exit Zone" tone={low ? 'red' : 'orange'} onClick={onExit} />
    </CardShell>
  );
}

function DoffingCard({ m, onOpen }) {
  return (
    <CardShell tone="red">
      <Who m={m} />
      <p className="text-xs text-neutral-500">Exited {formatTime(m.exitedZoneAt)}</p>
      <ActionButton icon={CheckCircle2} label="Doffing screening" tone="red" onClick={onOpen} />
    </CardShell>
  );
}

const ACTION_TONES = {
  neutral: 'border-neutral-200 bg-neutral-50 text-neutral-700 hover:border-neutral-300 dark:bg-ink-800 dark:border-neutral-700 dark:text-neutral-200',
  blue: 'border-seha-200 bg-seha-50 text-seha-700 dark:bg-seha-500/10 dark:text-seha-300 dark:border-seha-500/20',
  orange: 'border-orange-200 bg-orange-50 text-orange-700 dark:bg-orange-500/10 dark:text-orange-400 dark:border-orange-500/20',
  red: 'border-red-300 bg-red-50 text-red-700 dark:bg-red-500/10 dark:text-red-400 dark:border-red-500/20',
};

function ActionButton({ icon: Icon, label, tone, onClick }) {
  return (
    <button type="button" onClick={onClick}
      className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg border px-3 py-1.5 text-sm font-semibold transition-colors ${ACTION_TONES[tone]}`}>
      <Icon className="w-4 h-4" />
      {label}
    </button>
  );
}
